import type { ClientMessage, CommandSnapshot, DeploySnapshot, ServerMessage, Session, StoredEvent } from './protocol.ts';

export type LiveStatus = 'connecting' | 'live' | 'offline';

interface Subscription {
  /** Read at (re)connect time, so a catch-up always asks for the true gap. */
  sinceSeq: () => number;
  onEvent: (stored: StoredEvent) => void;
  onSynced: (seq: number) => void;
}

/**
 * One websocket for the whole app, with resubscribe-on-reconnect built in.
 *
 * Android will freeze this tab the moment the screen turns off and silently
 * rot the socket. Rather than trying to keep it alive, we assume it dies,
 * detect that on resume, and replay whatever was missed by sequence number.
 */
class Live {
  status: LiveStatus = 'offline';

  private ws: WebSocket | null = null;
  private subs = new Map<string, Subscription>();
  private sessionListeners = new Set<(session: Session) => void>();
  private deployListeners = new Set<(snapshot: DeploySnapshot) => void>();
  private commandListeners = new Set<(snapshot: CommandSnapshot) => void>();
  private desktopListeners = new Set<(online: string[]) => void>();
  private statusListeners = new Set<(status: LiveStatus) => void>();
  private retry = 0;
  private timer: number | null = null;
  private enabled = false;

  start(): void {
    if (this.enabled) return;
    this.enabled = true;
    addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.wake);
    this.open();
  }

  stop(): void {
    this.enabled = false;
    removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
    if (this.timer !== null) clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
    this.setStatus('offline');
  }

  private wake = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.ws?.readyState === WebSocket.OPEN) return;
    this.retry = 0;
    this.open();
  };

  private open(): void {
    if (!this.enabled) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.setStatus('connecting');

    const url = new URL('/ws', location.href);
    url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.retry = 0;
      this.setStatus('live');
      for (const [sessionId, sub] of this.subs) {
        this.send({ t: 'subscribe', sessionId, sinceSeq: sub.sinceSeq() });
      }
    };

    ws.onmessage = (ev: MessageEvent<string>) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      if (msg.t === 'event') {
        this.subs.get(msg.sessionId)?.onEvent({
          sessionId: msg.sessionId,
          seq: msg.seq,
          ts: msg.ts,
          event: msg.event,
        });
      } else if (msg.t === 'synced') {
        this.subs.get(msg.sessionId)?.onSynced(msg.seq);
      } else if (msg.t === 'session') {
        for (const fn of this.sessionListeners) fn(msg.session);
      } else if (msg.t === 'deploy') {
        for (const fn of this.deployListeners) fn(msg.snapshot);
      } else if (msg.t === 'command') {
        for (const fn of this.commandListeners) fn(msg.snapshot);
      } else if (msg.t === 'desktop') {
        for (const fn of this.desktopListeners) fn(msg.online);
      }
    };

    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.setStatus('offline');
      this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    if (!this.enabled || this.timer !== null) return;
    const delay = Math.min(15000, 500 * 2 ** this.retry++);
    this.timer = self.setTimeout(() => {
      this.timer = null;
      this.open();
    }, delay);
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  subscribe(sessionId: string, sub: Subscription): () => void {
    this.subs.set(sessionId, sub);
    this.send({ t: 'subscribe', sessionId, sinceSeq: sub.sinceSeq() });
    return () => {
      this.subs.delete(sessionId);
      this.send({ t: 'unsubscribe', sessionId });
    };
  }

  onSession(fn: (session: Session) => void): () => void {
    this.sessionListeners.add(fn);
    return () => this.sessionListeners.delete(fn);
  }

  onDeploy(fn: (snapshot: DeploySnapshot) => void): () => void {
    this.deployListeners.add(fn);
    return () => this.deployListeners.delete(fn);
  }

  onCommand(fn: (snapshot: CommandSnapshot) => void): () => void {
    this.commandListeners.add(fn);
    return () => this.commandListeners.delete(fn);
  }

  onDesktop(fn: (online: string[]) => void): () => void {
    this.desktopListeners.add(fn);
    return () => this.desktopListeners.delete(fn);
  }

  onStatus(fn: (status: LiveStatus) => void): () => void {
    this.statusListeners.add(fn);
    fn(this.status);
    return () => this.statusListeners.delete(fn);
  }

  private setStatus(status: LiveStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const fn of this.statusListeners) fn(status);
  }
}

export const live = new Live();
